import type { Session } from "@supabase/supabase-js";
import { initAnalytics, track } from "../analytics";

const STORAGE_KEY = "crimp:oauth-attempt:v1";
const CALLBACK_PARAM = "crimp_oauth_attempt";
const MAX_AGE_MS = 30 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type OAuthContext = { checkoutAttemptId?: string; trigger?: "equipment_limit" };
type PendingAttempt = OAuthContext & { id: string; startedAt: number };
export type OAuthAttempt = PendingAttempt & { redirectTo: string };
type Callback = {
  attempt: PendingAttempt;
  kind: "implicit" | "pkce" | "error" | "missing";
  errorCode?: string;
  /** Compare in memory only; never persisted or passed to analytics. */
  expectedAccessToken: string | null;
};

let callback: Callback | null = null;
let callbackCaptured = false;

// Error text can contain URLs, email addresses and provider credentials.
const ERROR_CODES = new Set([
  "access_denied", "disallowed_useragent", "invalid_request", "unauthorized_client",
  "unsupported_response_type", "invalid_scope", "server_error", "temporarily_unavailable",
  "provider_disabled", "provider_email_needs_verification", "signup_disabled",
  "bad_oauth_callback", "bad_oauth_state", "oauth_provider_not_supported",
  "flow_state_expired", "flow_state_not_found", "identity_already_exists",
  "identity_not_found", "unexpected_failure", "request_timeout", "over_request_rate_limit",
]);

function safeErrorCode(error: unknown): string {
  const code = typeof error === "string" ? error
    : error && typeof error === "object" && "code" in error ? error.code : undefined;
  return typeof code === "string" && ERROR_CODES.has(code) ? code : "unknown";
}

function forgetPending(): void {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Storage can be blocked. */ }
}

function readPending(): PendingAttempt | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    if (!value || typeof value.id !== "string" || !UUID.test(value.id)
      || typeof value.startedAt !== "number" || !Number.isFinite(value.startedAt)
      || value.startedAt > Date.now() || Date.now() - value.startedAt > MAX_AGE_MS) {
      forgetPending();
      return null;
    }
    return {
      id: value.id,
      startedAt: value.startedAt,
      ...(typeof value.checkoutAttemptId === "string" && UUID.test(value.checkoutAttemptId)
        ? { checkoutAttemptId: value.checkoutAttemptId } : {}),
      ...(value.trigger === "equipment_limit" ? { trigger: "equipment_limit" as const } : {}),
    };
  } catch {
    forgetPending();
    return null;
  }
}

function emit(event: string, attempt: PendingAttempt, properties: Record<string, unknown> = {}, instant = false): void {
  try {
    track(event, {
      provider: "google",
      auth_attempt_id: attempt.id,
      ...(attempt.checkoutAttemptId ? { checkout_attempt_id: attempt.checkoutAttemptId } : {}),
      ...(attempt.trigger ? { trigger: attempt.trigger } : {}),
      ...properties,
    }, { instant });
  } catch { /* Measurement must never interrupt sign-in. */ }
}

/** Same-tab storage follows the redirect without consuming another tab's login. */
export function beginOAuthAttempt(context: OAuthContext = {}): OAuthAttempt {
  const attempt: PendingAttempt = {
    id: crypto.randomUUID(),
    startedAt: Date.now(),
    ...(context.checkoutAttemptId && UUID.test(context.checkoutAttemptId)
      ? { checkoutAttemptId: context.checkoutAttemptId } : {}),
    ...(context.trigger === "equipment_limit" ? { trigger: context.trigger } : {}),
  };
  const redirect = new URL(window.location.href);
  redirect.searchParams.set(CALLBACK_PARAM, attempt.id);
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempt)); } catch { /* Sign-in still works. */ }
  emit("auth_oauth_initiated", attempt, {}, true);
  return { ...attempt, redirectTo: redirect.href };
}

export function failOAuthAttempt(attempt: OAuthAttempt, error: unknown): void {
  if (readPending()?.id === attempt.id) forgetPending();
  emit("auth_oauth_failed", attempt, { stage: "initiation", error_code: safeErrorCode(error) });
}

/** Must run before createClient: Supabase consumes callback credentials during initialization. */
export function captureOAuthCallback(): void {
  if (callbackCaptured || typeof window === "undefined") return;
  callbackCaptured = true;
  const url = new URL(window.location.href);
  const id = url.searchParams.get(CALLBACK_PARAM);
  if (!id) return;
  url.searchParams.delete(CALLBACK_PARAM);
  try { window.history.replaceState(window.history.state, "", url.href); } catch { /* Keep auth working. */ }
  const attempt = readPending();
  if (!attempt || attempt.id !== id) return;
  const hash = new URLSearchParams(url.hash.slice(1));
  // Match Supabase's query-over-fragment precedence.
  const get = (key: string) => url.searchParams.get(key) ?? hash.get(key);
  const hasError = Boolean(get("error") || get("error_code") || get("error_description"));
  callback = {
    attempt,
    kind: hasError ? "error" : get("access_token") && get("refresh_token") ? "implicit"
      : get("code") ? "pkce" : "missing",
    ...(hasError ? { errorCode: safeErrorCode(get("error_code") || get("error")) } : {}),
    expectedAccessToken: get("access_token"),
  };
  // Record arrival before SDK network work: a stalled session lookup must not
  // turn a real return into an apparent abandonment. Initialization is sync.
  try { void initAnalytics(); } catch { /* Measurement must never block auth. */ }
  emit("auth_oauth_returned", attempt, { callback_kind: callback.kind }, true);
  if (hasError) finishOAuthCallback(null);
}

export function hasOAuthCallback(): boolean { return callback !== null; }

/** Called after SDK initialization and identity reconciliation, never from its auth-lock callback. */
export function finishOAuthCallback(session: Session | null, error?: unknown): void {
  const observed = callback;
  if (!observed) return;
  // Consume before capture so getSession, remounts and auth notifications cannot double count.
  callback = null;
  forgetPending();
  const { attempt, kind } = observed;
  if (kind === "error" || error) {
    emit("auth_oauth_failed", attempt, {
      stage: "callback", error_code: observed.errorCode ?? safeErrorCode(error),
    });
    return;
  }
  const googleSession = session?.user.app_metadata?.provider === "google"
    || session?.user.identities?.some(identity => identity.provider === "google");
  const currentUrl = new URL(window.location.href);
  const credentialsConsumed = kind === "implicit" ? session?.access_token === observed.expectedAccessToken
    : kind === "pkce" && !currentUrl.searchParams.has("code") && !new URLSearchParams(currentUrl.hash.slice(1)).has("code");
  if (kind === "missing" || !googleSession || !credentialsConsumed) {
    emit("auth_oauth_failed", attempt, {
      stage: "session", error_code: kind === "missing" ? "missing_callback_credentials"
        : !googleSession ? "missing_google_session" : "callback_session_mismatch",
    });
    return;
  }
  emit("auth_oauth_succeeded", attempt);
}
